import { useEffect } from "react";

// React 19-safe head manager: imperatively syncs title + meta + canonical + OG/Twitter + JSON-LD.
// (react-helmet-async is unreliable on React 19, so we manage the document head directly.)

function upsertMeta(attr, key, content) {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, data) {
  let el = document.head.querySelector(`script[data-seo="${id}"]`);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export const Seo = ({ title, description, path = "/", settings, image, breadcrumbs, noindex = false, article = null }) => {
  const seo = settings?.seo || {};
  const base = (seo.canonicalBase || "https://acharya.holdings").replace(/\/$/, "");
  const fullTitle = title
    ? (seo.titleTemplate ? seo.titleTemplate.replace("%s", title) : `${title} — Acharya Holdings`)
    : (seo.defaultTitle || "Acharya Holdings — Independent Venture House");
  const desc = description || seo.defaultDescription || "Acharya Holdings is an independent venture house building and operating long-term businesses.";
  const canonical = `${base}${path}`;
  const ogImage = image || seo.ogImage || "/brand/og-image.png";
  const companyName = settings?.companyName || "Acharya Holdings";
  const linkedin = settings?.social?.linkedin;
  const hasAddress = !!settings?.address;
  const addr = settings?.address || {};
  const bcKey = breadcrumbs ? breadcrumbs.map((b) => b.path).join("|") : "";
  const articleKey = article ? `${article.headline}|${article.datePublished}|${article.dateModified}` : "";

  useEffect(() => {
    document.title = fullTitle;
    upsertMeta("name", "description", desc);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
    upsertLink("canonical", canonical);

    upsertMeta("property", "og:type", article ? "article" : "website");
    upsertMeta("property", "og:site_name", companyName);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", ogImage);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", desc);
    upsertMeta("name", "twitter:image", ogImage);

    setJsonLd("organization", {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: companyName,
      url: base,
      logo: `${base}/brand/logo-icon.webp`,
      description: seo.defaultDescription,
      sameAs: linkedin ? [linkedin] : [],
      ...(hasAddress ? {
        address: {
          "@type": "PostalAddress",
          streetAddress: [addr.line1, addr.line2].filter(Boolean).join(", "),
          addressLocality: "Nashik",
          addressRegion: "Maharashtra",
          postalCode: "422013",
          addressCountry: "IN",
        },
      } : {}),
    });

    setJsonLd("breadcrumb", breadcrumbs && breadcrumbs.length > 0 ? {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.name,
        item: `${base}${b.path}`,
      })),
    } : null);

    setJsonLd("article", article ? {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline: article.headline,
      description: article.description || undefined,
      image: article.image ? [article.image] : undefined,
      datePublished: article.datePublished || undefined,
      dateModified: article.dateModified || article.datePublished || undefined,
      mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
      publisher: {
        "@type": "Organization",
        name: companyName,
        logo: { "@type": "ImageObject", url: `${base}/brand/logo-icon.webp` },
      },
      author: { "@type": "Organization", name: companyName },
    } : null);

    return () => { setJsonLd("breadcrumb", null); setJsonLd("article", null); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullTitle, desc, canonical, ogImage, companyName, base, linkedin, hasAddress, bcKey, noindex, articleKey]);

  return null;
};
